import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import SignInWithGoogleButton from '../../components/SignInWithGoogleButton';
import SignInWithTwitchButton from '../../components/SignInWithTwitchButton';
import {
  claimLscMemberInvite,
  LscApiError,
  previewLscMemberInvite,
} from '../api';
import type { LscMemberInvitePreview } from '../types';
import { lscPath } from '../routing';
import LoadingState from '../components/LoadingState';

const ASK_COMMISSIONER = "This invite link isn't valid. Ask your commissioner for a new one.";

function plainMessage(error: unknown): string {
  if (error instanceof LscApiError && error.message && !error.message.startsWith('Request failed')) {
    return error.message;
  }
  return ASK_COMMISSIONER;
}

const LscMemberInviteClaimPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading, refreshUser } = useUser();
  const [preview, setPreview] = useState<LscMemberInvitePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const claimStarted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    setPreview(null);
    setClaimError(null);
    claimStarted.current = false;
    if (!token) {
      setPreview({ valid: false, message: ASK_COMMISSIONER, reason: 'invalid' });
      setLoadingPreview(false);
      return;
    }
    previewLscMemberInvite(token)
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch((error) => {
        if (!cancelled) {
          setPreview({ valid: false, message: plainMessage(error), reason: 'invalid' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const finishClaim = useCallback(async () => {
    if (!token || claiming) return;
    setClaiming(true);
    setClaimError(null);
    try {
      const result = await claimLscMemberInvite(token);
      await refreshUser();
      navigate(lscPath(`/leagues/${result.league_id}`));
    } catch (error) {
      setClaimError(plainMessage(error));
      setClaiming(false);
    }
  }, [claiming, navigate, refreshUser, token]);

  const signInMethod = preview?.sign_in_method || (preview?.requires_twitch === false ? 'google' : 'twitch');
  const requiresTwitch = signInMethod === 'twitch';
  const signedInForClaim = requiresTwitch
    ? Boolean(user?.twitch_user_id)
    : Boolean(user?.google_connected);

  useEffect(() => {
    if (loading || loadingPreview || !preview?.valid || !signedInForClaim) return;
    if (claimStarted.current) return;
    claimStarted.current = true;
    void finishClaim();
  }, [finishClaim, loading, loadingPreview, preview, signedInForClaim]);

  const returnTo =
    typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/lsc/claim';

  return (
    <div
      className="min-h-screen bg-secondary-900 text-white flex items-center justify-center px-4 py-16"
      data-testid="member-invite-claim-page"
    >
      <div className="w-full max-w-lg rounded-2xl border border-secondary-700 bg-secondary-800/80 p-8 space-y-6">
        {loading || loadingPreview ? (
          <LoadingState message="Checking your invite..." />
        ) : preview?.valid ? (
          <>
            <p className="text-xs uppercase tracking-wide text-secondary-400">League Stream Center</p>
            <h1
              className="text-2xl font-semibold leading-snug"
              data-testid="member-invite-claim-headline"
            >
              {preview.headline}
            </h1>
            <p className="text-sm text-secondary-300">
              {requiresTwitch
                ? 'Sign in with Twitch to create your account and link your channel.'
                : 'Sign in with Google to claim this seat. Your league name stays as it is.'}
            </p>
            {claimError ? (
              <p className="text-sm text-amber-200" role="alert" data-testid="member-invite-claim-message">
                {claimError}
              </p>
            ) : null}
            {claiming ? (
              <p className="text-sm text-secondary-300" data-testid="member-invite-claim-pending">
                {requiresTwitch ? 'Linking your Twitch account...' : 'Claiming your seat...'}
              </p>
            ) : signedInForClaim ? null : requiresTwitch ? (
              <div data-testid="member-invite-twitch-signin">
                <SignInWithTwitchButton returnTo={returnTo} />
              </div>
            ) : (
              <div data-testid="member-invite-google-signin">
                <SignInWithGoogleButton returnTo={returnTo} />
              </div>
            )}
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold" data-testid="member-invite-claim-headline">
              Invite link
            </h1>
            <p className="text-sm text-amber-200" role="alert" data-testid="member-invite-claim-message">
              {preview?.message || ASK_COMMISSIONER}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default LscMemberInviteClaimPage;
